import { TextKey } from '@mews-commander/enums';
import { LocalizedText, useAppClockService } from '@mews/framework';
import { ReleaseStrategy } from '@mews-commander/data-access';
import { ResponsiveTwoColumns } from '@mews-commander/ui';
import { getTodayStructuredDate } from '@mews-commander/legacy-entities/src/selectors/session/dateTime';
import { isCurrentEnterpriseAvailabilityBlockAdvancedFeaturesEnabled } from '@mews-commander/legacy-entities/src/selectors/session/subscription';
import { useCommanderSelector } from '@mews-commander/utils';
import {
    useFormValueSelector,
    SelectionCardGroupField,
    StepperField,
    DateTimePickerField,
    TimeStringPickerField,
} from '@mews-commander/legacy-form';
import { getMaxReleaseDate } from '../selectors';
import { FormValues } from '../types';

interface OwnProps {
    form: string;
    disabled?: boolean;
}

export const ReleaseDateField = (props: OwnProps) => {
    const releaseStrategy = useFormValueSelector<FormValues>(props.form, 'blockReleaseStrategy');

    const appClockService = useAppClockService();

    const hasBlockAdvancedFeatures = useCommanderSelector(isCurrentEnterpriseAvailabilityBlockAdvancedFeaturesEnabled);
    const minReleaseDate = useCommanderSelector((state) => getTodayStructuredDate(state, { appClockService }));
    const maxReleaseDate = useCommanderSelector((state) => getMaxReleaseDate(state, props));

    if (!hasBlockAdvancedFeatures) {
        return (
            <DateTimePickerField
                label={<LocalizedText textKey={TextKey.ReleaseAt} />}
                minDate={minReleaseDate}
                maxDate={maxReleaseDate}
                required
                {...props}
            />
        );
    }

    const blockReleaseStrategyOptions = [
        {
            label: <LocalizedText textKey={TextKey.BlockReleaseStrategyOneTime} />,
            name: ReleaseStrategy.FixedReleaseUtc,
            value: ReleaseStrategy.FixedReleaseUtc,
            dataTestId: ReleaseStrategy.FixedReleaseUtc,
        },
        {
            label: <LocalizedText textKey={TextKey.BlockReleaseStrategyEveryDay} />,
            name: ReleaseStrategy.RollingReleaseOffset,
            value: ReleaseStrategy.RollingReleaseOffset,
            dataTestId: ReleaseStrategy.RollingReleaseOffset,
        },
        {
            label: <LocalizedText textKey={TextKey.BlockReleaseStrategyDoNotRelease} />,
            name: ReleaseStrategy.None,
            value: ReleaseStrategy.None,
            dataTestId: ReleaseStrategy.None,
        },
    ];

    return (
        <>
            <SelectionCardGroupField
                name="blockReleaseStrategy"
                label={<LocalizedText textKey={TextKey.ReleaseAvailabilityBlock} />}
                selectOptionGroupType="radio"
                templateColumns={`repeat(${blockReleaseStrategyOptions.length}, 1fr)`}
                required
                options={blockReleaseStrategyOptions}
            />
            {releaseStrategy === ReleaseStrategy.FixedReleaseUtc && (
                <DateTimePickerField
                    label={<LocalizedText textKey={TextKey.ReleaseAt} />}
                    minDate={minReleaseDate}
                    maxDate={maxReleaseDate}
                    required
                    {...props}
                />
            )}
            {releaseStrategy === ReleaseStrategy.RollingReleaseOffset && (
                <ResponsiveTwoColumns>
                    <StepperField
                        name="offsetDays"
                        label={<LocalizedText textKey={TextKey.ReleaseInAdvanceOf} />}
                        suffix={<LocalizedText textKey={TextKey.DayPlural} />}
                        min={0}
                        required
                    />
                    <TimeStringPickerField
                        name="offsetTime"
                        label={<LocalizedText textKey={TextKey.Time} />}
                        required
                    />
                </ResponsiveTwoColumns>
            )}
        </>
    );
};
